import React, { useRef } from 'react'
import CatalogoBrand from '../../molecules/CatalogoBrand/CatalogoBrand'

import { Navigation, Pagination, A11y } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import Link from 'next/link'
import Button from '../../atoms/Button/Button'
import { Breadcrumbs, MetaSeo, Trans } from '../../../components/index'
import { getAltText } from '../../../utils/safe'

const AboutTemplate = ({ page, data }) => {
  const scrollDownRef = useRef(null)
  return (
    <div className="page about-template">
      <MetaSeo seo={page?.seo} />
      <div className="w-container">
        <Breadcrumbs breadcrumbs={page?.breadcrumbs} />
        <h1 className="page__title">{page?.titolo}</h1>
        {data && <AboutTopBox scrollDownRef={scrollDownRef} data={data} />}
        {data &&
          data?.children.map((item) => (
            <>
              {item?.key === 'about-perche' && <AboutCategories data={item} />}
              {item?.key === 'banners' && <AboutBrands data={item} />}
            </>
          ))}
      </div>
      {data &&
        data?.children.map(
          (item) => item?.key === 'importatori' && <AboutImportatori data={item} />
        )}
      <div className="w-container">
        {data &&
          data?.children?.map((item) => (
            <>
              {item?.key === 'specialisti' && <AboutPartnership data={item} />}
              {(item?.key === 'storia' || item?.key === 'certificazioni') && (
                <AboutStoria scrollDownRef={scrollDownRef} data={item} />
              )}
              {item?.key === 'filiera' && <AboutRuoli data={item} />}
            </>
          ))}
      </div>
    </div>
  )
}

const AboutTopBox = ({ scrollDownRef, data }) => {
  return (
    <div className="about-top-box">
      <div className="about-top-box__content">
        <div
          dangerouslySetInnerHTML={{
            __html: data?.description_html,
          }}
        ></div>
        {data?.cta && (
          <Button
            variant="ghost"
            size="medium"
            label={data?.cta}
            className="about-top-box__cta"
            onClick={() => scrollDownRef.current.scrollIntoView()}
          />
        )}
      </div>
      <div className="about-top-box__image-box">
        {data?.image && (
          <img
            src={data?.image?.url}
            alt={getAltText(data?.image)}
            className="about-top-box__image"
          />
        )}
        <div className="about-top-box__caption">{data?.description}</div>
      </div>
    </div>
  )
}

const AboutCategoriesSlide = ({ slide }) => {
  return (
    <div className="about-categories-slide">
      <div className="about-categories-slide__image-box">
        <img
          className="about-categories-slide__image"
          src={slide?.image?.url}
          alt={getAltText(slide?.image)}
        />
      </div>
      <p className="about-categories-slide__title">{slide.name}</p>
      <div
        className="about-categories-slide__description"
        dangerouslySetInnerHTML={{
          __html: slide?.description_html,
        }}
      ></div>
    </div>
  )
}

const AboutCategories = ({ data }) => {
  return (
    <div className="about-categories">
      <h2 className="about-categories__title">{data?.name}</h2>
      <div className="about-categories__slider swiper--with-pagination">
        <Swiper
          id={'about-categories-slider'}
          modules={[A11y, Pagination, Navigation]}
          navigation={true}
          pagination={{ clickable: true }}
          spaceBetween={30}
          slidesPerView={4}
          breakpoints={{
            320: {
              slidesPerView: 1,
            },
            576: {
              slidesPerView: 2,
            },
            991: {
              slidesPerView: 4,
            },
          }}
        >
          {data?.children.map((slide, index) => {
            return (
              <SwiperSlide key={index}>
                <AboutCategoriesSlide slide={slide} />
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
      {data?.final_url && (
        <div className="about-categories__cta-box">
          <Link legacyBehavior href={data?.final_url}>
            <a className="about-categories__cta">
              <Button label={data?.cta} size="large" variant="secondary" />
            </a>
          </Link>
        </div>
      )}
    </div>
  )
}

const AboutBrands = ({ data }) => {
  return (
    <div className="about-brands">
      <h2 className="about-brands__title">{data?.name}</h2>
      <div className="about-brands__list">
        {data?.children.map((banner) => (
          <div className="about-brands__item">
            <CatalogoBrand
              img={banner?.image}
              title={banner?.name}
              description={banner?.description}
              cta={banner?.cta}
              url={banner?.final_url}
              img_size={banner?.image_size}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

const AboutImportatori = ({ data }) => {
  return (
    <div className="about-importatori">
      <div className="w-container">
        <h2 className="about-importatori__title">{data?.name}</h2>
        <p className="about-importatori__description">{data?.description}</p>
        <div className="about-importatori__row">
          <div className="about-importatori__content">
            {data?.children?.map((card) => (
              <div className="about-importatori__card">
                <p className="about-importatori__card-title">{card?.name}</p>
                <p className="about-importatori__card-description">
                  {card?.description}
                  {'.'}
                </p>
              </div>
            ))}
          </div>
          <div className="about-importatori__images-box">
            {data?.image && (
              <img
                src={data?.image?.url}
                alt={getAltText(data?.image)}
                className="about-importatori__image"
              />
            )}
            {data?.children[0]?.image && (
              <img
                src={data?.children[0]?.image?.url}
                alt={getAltText(data?.children[0]?.image)}
                className="about-importatori__image"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const AboutPartnership = ({ data }) => {
  return (
    <div className="about-partnership">
      <h2 className="about-partnership__title">{data?.name}</h2>
      <div className="about-partnership__row">
        <div className="about-partnership__content">
          <div className="about-partnership__description">{data?.description}</div>
          {data?.final_url && (
            <Link legacyBehavior href={data?.final_url}>
              <a>
                <Button
                  variant="primary"
                  size="medium"
                  label={data?.cta}
                  className="about-partnership__cta about-partnership__cta--desktop"
                />
              </a>
            </Link>
          )}
        </div>
        <div className="about-partnership__vantaggi">
          {data?.children?.map((vantaggio) => (
            <p className="about-partnership__vantaggi-item">{vantaggio?.name}</p>
          ))}
        </div>
        <div className="about-partnership__cta-box">
          {data?.final_url && (
            <Link legacyBehavior href={data?.final_url}>
              <a>
                <Button
                  variant="primary"
                  size="medium"
                  label={data?.cta}
                  className="about-partnership__cta about-partnership__cta--mobile"
                />
              </a>
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

const AboutStoriaSlide = ({ slide }) => {
  return (
    <div className="about-storia-slide">
      <div className="about-storia-slide__content">
        <p className="about-storia-slide__year">{slide?.anno}</p>
        <p className="about-storia-slide__title">{slide?.name}</p>
        <div
          className="about-storia-slide__description"
          dangerouslySetInnerHTML={{
            __html: slide?.description_html,
          }}
        ></div>
      </div>
      <div className="about-storia-slide__image-box">
        {slide?.image && (
          <img
            src={slide?.image?.url}
            alt={getAltText(slide?.image)}
            className="about-storia-slide__image"
          />
        )}
      </div>
    </div>
  )
}

const AboutStoria = ({ scrollDownRef, data }) => {
  return (
    <div className="about-storia" ref={scrollDownRef}>
      <h2 className="about-storia__title">{data?.name}</h2>
      <div className="about-storia__slider">
        <Swiper
          id={'about-storia-slider'}
          modules={[A11y, Pagination, Navigation]}
          navigation={true}
          pagination={{ clickable: true }}
          slidesPerView={1}
        >
          {data?.children.map((slide, index) => {
            return (
              <SwiperSlide key={index}>
                <AboutStoriaSlide slide={slide} />
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
    </div>
  )
}

const AboutRuoli = ({ data }) => {
  return (
    <div className="about-ruoli">
      <div className="about-ruoli__container">
        <div className="about-ruoli__image-box">
          {data?.image && (
            <img
              src={data?.image?.url}
              alt={getAltText(data?.image) || data?.description}
              className="about-ruoli__image"
            />
          )}
        </div>
        <div className="about-ruoli__content">
          <h2 className="about-ruoli__title">{data?.name}</h2>
          <div
            className="about-ruoli__description"
            dangerouslySetInnerHTML={{
              __html: data?.description_html,
            }}
          ></div>
        </div>
      </div>
    </div>
  )
}

export default AboutTemplate
